import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
	listMyQuotations,
	listMyChallans,
	uploadFile,
	uploadChallan,
	uploadInvoice,
} from '../api/vendorApi';
import { vendorImagePath } from '../api/vendorAxios';
import { VpSpinner, VpEmpty, VpPagination, money, fmtDate } from '../components/VpUI';
import VpIcon from '../components/VpIcon';

const PER_PAGE = 10;

const VendorChallans = () => {
	const [loading, setLoading] = useState(true);
	const [accepted, setAccepted] = useState<any[]>([]);
	const [selectedQuote, setSelectedQuote] = useState('');
	const [docs, setDocs] = useState<any[]>([]);
	const [docTotal, setDocTotal] = useState(0);
	const [docPage, setDocPage] = useState(1);
	const [typeFilter, setTypeFilter] = useState('');
	const [docType, setDocType] = useState<'challan' | 'invoice'>('challan');
	const [docNo, setDocNo] = useState('');
	const [amount, setAmount] = useState('');
	const [note, setNote] = useState('');
	const [files, setFiles] = useState<string[]>([]);
	const [uploading, setUploading] = useState(false);
	const [saving, setSaving] = useState(false);

	// Load accepted quotations (for the dropdown) + the paginated document list.
	const loadDocs = useCallback(async () => {
		const myDocs = await listMyChallans({
			pageNo: docPage,
			perPage: PER_PAGE,
			type: typeFilter || undefined,
		});
		setDocs(myDocs?.records || []);
		setDocTotal(myDocs?.totalRecords || 0);
	}, [docPage, typeFilter]);

	const loadAll = useCallback(async () => {
		try {
			// Pull a large page of quotations to gather all accepted ones for the dropdown.
			const quotes = await listMyQuotations({ pageNo: 1, perPage: 500 });
			const acc = (quotes?.records || []).filter(
				(q: any) => q.quotation_status === 'confirmed',
			);
			setAccepted(acc);
			setSelectedQuote((prev) => prev || (acc.length ? acc[0]._id : ''));
			await loadDocs();
		} catch (err: any) {
			toast.error(err?.apiMessage || 'Could not load your data.');
		} finally {
			setLoading(false);
		}
	}, [loadDocs]);

	useEffect(() => {
		loadAll();
	}, [loadAll]);

	useEffect(() => {
		loadDocs();
	}, [loadDocs]);

	const onPickFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const picked = Array.from(e.target.files || []);
		if (!picked.length) return;
		setUploading(true);
		try {
			const paths: string[] = [];
			for (const f of picked) {
				const p = await uploadFile(f);
				paths.push(p);
			}
			setFiles((prev) => [...prev, ...paths]);
		} catch (err: any) {
			toast.error(err?.apiMessage || 'File upload failed.');
		} finally {
			setUploading(false);
			e.target.value = '';
		}
	};

	const removeFile = (idx: number) => setFiles((prev) => prev.filter((_, i) => i !== idx));

	const submit = async () => {
		if (!selectedQuote) {
			toast.warn('Select an accepted quotation.');
			return;
		}
		if (!files.length) {
			toast.warn('Please upload at least one file.');
			return;
		}
		setSaving(true);
		try {
			const payload = {
				quotationId: selectedQuote,
				document_no: docNo || undefined,
				amount: amount ? Number(amount) : undefined,
				note: note || undefined,
				files,
			};
			const res =
				docType === 'invoice' ? await uploadInvoice(payload) : await uploadChallan(payload);
			toast.success(
				`${docType === 'invoice' ? 'Invoice' : 'Challan'} uploaded successfully!`,
			);
			// Soft warning when the amount exceeds the quotation total.
			if (res?.warning) {
				toast.warn(res.warning, { autoClose: 8000 });
			}
			setDocNo('');
			setAmount('');
			setNote('');
			setFiles([]);
			await loadAll();
		} catch (err: any) {
			toast.error(err?.apiMessage || 'Could not upload.');
		} finally {
			setSaving(false);
		}
	};

	if (loading) return <VpSpinner />;

	if (!accepted.length) {
		return (
			<div className='vp-card'>
				<div className='vp-card-head'>
					<h3>Challans &amp; Invoices</h3>
				</div>
				<VpEmpty
					icon='challans'
					text='You can upload challans and invoices once one of your quotations is accepted.'
				/>
			</div>
		);
	}

	return (
		<div className='vp-grid-2'>
			<div className='vp-card vp-card-pad'>
				<h3 style={{ marginTop: 0 }}>Upload Challan / Invoice</h3>

				<div className='vp-field'>
					<label className='vp-label'>Accepted Quotation</label>
					<select
						className='vp-input'
						value={selectedQuote}
						onChange={(e) => setSelectedQuote(e.target.value)}>
						{accepted.map((q) => (
							<option key={q._id} value={q._id}>
								{q.quotation_no} — {q.requisition?.requisition_no || 'Requisition'}
							</option>
						))}
					</select>
				</div>

				<div className='vp-field'>
					<label className='vp-label'>Document Type</label>
					<select
						className='vp-input'
						value={docType}
						onChange={(e) => setDocType(e.target.value as 'challan' | 'invoice')}>
						<option value='challan'>Challan (multiple allowed)</option>
						<option value='invoice'>Invoice (one per quotation)</option>
					</select>
				</div>

				<div className='vp-field'>
					<label className='vp-label'>Document No.</label>
					<input
						className='vp-input'
						value={docNo}
						onChange={(e) => setDocNo(e.target.value)}
						placeholder={docType === 'invoice' ? 'INV-001' : 'CH-001'}
					/>
				</div>

				<div className='vp-field'>
					<label className='vp-label'>Amount (optional)</label>
					<input
						className='vp-input'
						type='number'
						value={amount}
						onChange={(e) => setAmount(e.target.value)}
						placeholder='0.00'
					/>
				</div>

				<div className='vp-field'>
					<label className='vp-label'>Note (optional)</label>
					<input
						className='vp-input'
						value={note}
						onChange={(e) => setNote(e.target.value)}
						placeholder='Any note'
					/>
				</div>

				<div className='vp-field'>
					<label className='vp-label'>Files</label>
					<input
						type='file'
						multiple
						accept='image/*,application/pdf'
						onChange={onPickFiles}
					/>
					{uploading && (
						<div className='vp-muted' style={{ marginTop: 6 }}>
							Uploading…
						</div>
					)}
					<div>
						{files.map((f, i) => (
							<span key={i} className='vp-file-chip'>
								<VpIcon name='attach' size={13} /> {f.split('/').pop()}
								<span
									style={{ cursor: 'pointer', color: 'var(--vp-danger)' }}
									onClick={() => removeFile(i)}>
									✕
								</span>
							</span>
						))}
					</div>
				</div>

				<button
					type='button'
					className='vp-btn vp-btn-primary vp-btn-block'
					onClick={submit}
					disabled={saving || uploading}>
					{saving ? 'Saving…' : `Upload ${docType === 'invoice' ? 'Invoice' : 'Challan'}`}
				</button>
			</div>

			<div className='vp-card'>
				<div className='vp-card-head'>
					<h3>Uploaded Documents</h3>
					<select
						className='vp-input vp-input-sm'
						style={{ width: 150 }}
						value={typeFilter}
						onChange={(e) => {
							setTypeFilter(e.target.value);
							setDocPage(1);
						}}>
						<option value=''>All types</option>
						<option value='challan'>Challans</option>
						<option value='invoice'>Invoices</option>
					</select>
				</div>
				{docs.length === 0 ? (
					<VpEmpty icon='document' text='No documents uploaded yet.' />
				) : (
					<div className='vp-table-wrap'>
						<table className='vp-table'>
							<thead>
								<tr>
									<th>Type</th>
									<th>Quotation</th>
									<th>No.</th>
									<th>Date</th>
									<th style={{ textAlign: 'right' }}>Amount</th>
									<th>Files</th>
								</tr>
							</thead>
							<tbody>
								{docs.map((d) => (
									<tr key={d._id}>
										<td style={{ textTransform: 'capitalize' }}>{d.type}</td>
										<td>{d.quotation_no || '—'}</td>
										<td>{d.document_no || '—'}</td>
										<td>{fmtDate(d.document_date || d.createdAt)}</td>
										<td style={{ textAlign: 'right' }}>
											{d.amount ? money(d.amount) : '—'}
											{d.amountExceedsQuotation && (
												<span
													title='Amount exceeds the quotation total'
													style={{
														marginLeft: 6,
														cursor: 'help',
														color: 'var(--vp-warning)',
													}}>
													<VpIcon name='warning' size={14} />
												</span>
											)}
										</td>
										<td>
											{(d.files || []).map((f: string, i: number) => (
												<a
													key={i}
													href={vendorImagePath(f)}
													target='_blank'
													rel='noreferrer'
													className='vp-file-chip'>
													<VpIcon name='attach' size={13} /> {i + 1}
												</a>
											))}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
				<VpPagination
					page={docPage}
					perPage={PER_PAGE}
					total={docTotal}
					onPage={setDocPage}
				/>
			</div>
		</div>
	);
};

export default VendorChallans;
